import { atom } from "jotai";
import { apiService } from "@/api";
import type { CurrentReservoirState } from "@/api/model/current-reservoir-state";
import { TimeoutTimer } from "@/utils/timer";
import { withOperationHandlingAtom } from "../operations";
import { DATA_FETCH_INTERVAL_MILLIS } from "./constants";
import { selectedPropertyIdAtom } from "./property-domain";

export const currentReservoirStatesAtom = atom<CurrentReservoirState<{ reservoir: string }>[]>([]);
currentReservoirStatesAtom.debugLabel = "currentReservoirStatesAtom";

export const currentReservoirStatesLoadingAtom = atom<boolean>(false);
currentReservoirStatesLoadingAtom.debugLabel = "currentReservoirStatesLoadingAtom";
export const currentReservoirStatesErrorAtom = atom<string | null>(null);
currentReservoirStatesErrorAtom.debugLabel = "currentReservoirStatesErrorAtom";

export const currentReservoirStatesByPropertyAtom = atom((get) => {
  const selectedPropertyId = get(selectedPropertyIdAtom);
  const states = get(currentReservoirStatesAtom);
  if (!selectedPropertyId) return [] as CurrentReservoirState[];
  return states as unknown as CurrentReservoirState[];
});
currentReservoirStatesByPropertyAtom.debugLabel = "currentReservoirStatesByPropertyAtom";

export const currentReservoirStateByReservoirIdAtom = atom(
  (get) =>
    (reservoirId: string): CurrentReservoirState<{ reservoir: string }> | null => {
      const states = get(currentReservoirStatesAtom);
      return states.find((s) => (s.reservoir as unknown as string) === reservoirId) || null;
    }
);
currentReservoirStateByReservoirIdAtom.debugLabel = "currentReservoirStateByReservoirIdAtom";

export const fetchCurrentReservoirStatesAtom = atom(null, async (get, set) => {
  const selectedPropertyId = get(selectedPropertyIdAtom);
  if (!selectedPropertyId) return;
  await set(
    withOperationHandlingAtom,
    {
      operation: "fetchCurrentReservoirStates",
      message: "Carregando estado atual dos reservatórios...",
      displayLoading: false,
      displayError: true,
    },
    async () => {
      set(currentReservoirStatesLoadingAtom, true);
      set(currentReservoirStatesErrorAtom, null);
      try {
        const states = await apiService.currentReservoirState.getByProperty(selectedPropertyId);
        set(currentReservoirStatesAtom, states);
        return states;
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Unknown error";
        set(currentReservoirStatesErrorAtom, msg);
        throw e;
      } finally {
        set(currentReservoirStatesLoadingAtom, false);
      }
    }
  );
});
fetchCurrentReservoirStatesAtom.debugLabel = "fetchCurrentReservoirStatesAtom";
fetchCurrentReservoirStatesAtom.onMount = (dispatch) => {
  const timer = new TimeoutTimer();
  timer.start(DATA_FETCH_INTERVAL_MILLIS, async () => {
    await dispatch();
  });
  return () => timer.stop();
};
